import { randomInteger } from "../../../../packages/common/src";

import { readImageUrlAsBase64 } from "./data_source/getImageBase64";

import type {
  ExcalidrawElement,
  ExcalidrawImageElement,
  FileId,
  OrderedExcalidrawElement,
} from "../../../../packages/element/src/types";
import type { ExcalidrawImperativeAPI } from "../../../../packages/excalidraw/types";
import type { Radians } from "../../../../packages/math/src";

const lastVersion = 0;

const isOfficeFile = false;
const generateImageElement = ({
  fileId,
  x = 100,
  y = 100,
  width = 200,
  height = 200,
  url,
}: {
  fileId: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  url: string;
}): ExcalidrawImageElement => {
  return {
    index: null,
    crop: null,
    id: fileId,
    type: "image",
    x,
    y,
    width,
    height,
    angle: 0 as Radians,
    strokeColor: "transparent",
    backgroundColor: "transparent",
    fillStyle: "hachure",
    strokeWidth: 1,
    strokeStyle: "solid",
    roughness: 1,
    opacity: 100,
    groupIds: [],
    roundness: null,
    seed: randomInteger(),
    version: lastVersion + 1,
    versionNonce: randomInteger(),
    isDeleted: false,
    boundElements: null,
    updated: Date.now(),
    link: null,
    status: "pending",
    fileId: fileId as any,
    scale: [1, 1],
    locked: isOfficeFile, // if office file then lock it by default.
    frameId: null,
    //@ts-ignore
    navigatedVersion: 0,
    url,
  } as ExcalidrawImageElement;
};

const addImageOrShowImage = (
  elements: OrderedExcalidrawElement[],
  {
    fileId,
    x = 100,
    y = 100,
    width = 200,
    height = 200,
    url,
  }: {
    fileId: string;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    url: string;
  },
): ExcalidrawElement[] => {
  let index: number | undefined;
  if (
    elements.some((ele, _index) => {
      //@ts-ignore
      if (ele?.url === url) {
        index = _index;
        return true;
      }
      return false;
    })
  ) {
    const ele = elements[index!];
    elements[index!] = {
      ...ele,
      isDeleted: false,
      version: ele.version + 1,
      //@ts-ignore
      navigatedVersion: ele.version + 1,
    };

    return elements;
  }

  (elements as ExcalidrawElement[]).push(
    generateImageElement({
      fileId,
      x,
      y,
      width,
      height,
      url,
    }),
  );
  return elements;
};
export const loadImage = async (
  excalidrawAPI: ExcalidrawImperativeAPI,
  url: string,
) => {
  // debugger;
  const { dataURL, name, width, height } = await readImageUrlAsBase64(url);
  const elements = excalidrawAPI
    .getSceneElementsIncludingDeleted()
    .map((ele) => {
      //@ts-ignore
      if (ele?.url) {
        return {
          ...ele,
          isDeleted: true,
          version: ele.version + 1,
          navigatedVersion: ele.version + 1,
        };
      }
      return ele;
    });
  // Add the new image file
  excalidrawAPI.addFiles([
    {
      id: name as FileId,
      dataURL,
      mimeType: "image/png",
      created: Date.now(),
    },
  ]);
  // Add the new image element
  excalidrawAPI.updateScene({
    elements: addImageOrShowImage(elements, {
      fileId: name,
      height,
      width,
      url,
    }),
  });
};

/**
 * ### docs:
 * create and trigger file pick form input element
 *
 * to show the user select file dialog
 */
export async function loadPDf(): Promise<File> {
  return new Promise((resolve, reject) => {
    try {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "application/pdf";
      input.onchange = (e) => {
        const target = e.target as HTMLInputElement;
        if (!target || !target.files || target.files.length === 0) {
          reject(new Error("No file selected"));
          return;
        }
        const file = target.files[0];
        if (!file) {
          reject(new Error("File is invalid"));
          return;
        }
        if (file.type !== "application/pdf") {
          reject(new Error("Selected file is not a PDF"));
          return;
        }
        resolve(file);
      };
      input.onabort = () => {
        reject(null);
      };
      input.oncancel = () => {
        reject(null);
      };
      input.onerror = (error) => {
        reject(error || new Error("An error occurred during file selection"));
      };
      input.click();
    } catch (error) {
      reject(error);
    }
  });
}
